import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth.store';
import { SiteRoles, ProjRoles, OrgRoles, BOQRoles } from '@/types/api/roles.types';

import i18n from '@/plugins/vue-i18n';

export type RoleType = 'site' | 'project' | 'org' | 'boq';
export type RoleCode = SiteRoles | ProjRoles | OrgRoles | BOQRoles;

const { t } = i18n.global;

const ROLE_ORDER: Record<RoleType, RoleCode[]> = {
  project: [
    ProjRoles.PROJ_MNGR,
    ProjRoles.PROJ_PROC_OFF,
    ProjRoles.PROJ_PROF,
    ProjRoles.PROJ_MEMB,
    ProjRoles.PROJ_CLI_BASE,
    ProjRoles.PROJ_CLI_APRV,
  ],
  site: [
    SiteRoles.SITE_ADMIN,
    SiteRoles.SITE_PROC_OFF,
    SiteRoles.SITE_FIN_OFF,
    SiteRoles.SITE_PROF,
    SiteRoles.SITE_FLD_USR,
    SiteRoles.SITE_MEMBER,
    SiteRoles.SITE_CLI_BASE,
    SiteRoles.SITE_CLI_APRV,
  ],
  org: [OrgRoles.ORG_ADMIN, OrgRoles.ORG_OWN, OrgRoles.ORG_FO, OrgRoles.ORG_FA, OrgRoles.ORG_MEMB],
  boq: [BOQRoles.BOQ_OWN, BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_CAT_ADMIN],
};

/**
 * Sorts an array of role codes or user objects according to the predefined order
 * @param type The type of role ('site', 'project', 'org', or 'boq')
 * @param items Array of role codes or user objects to sort
 * @param options Optional configuration for user object sorting
 * @returns Sorted array
 */
export const sortRolesByOrder = <T>(
  type: RoleType,
  items: T[],
  options?: {
    getRoleCode?: (item: T) => RoleCode;
    getPendingStatus?: (item: T) => boolean;
    userExistsOnPlatform?: (item: T) => boolean;
  },
): T[] => {
  const order = ROLE_ORDER[type];
  if (!order) return items;

  return items.sort((a, b) => {
    if (options?.getRoleCode) {
      // Handle pending status
      if (options.getPendingStatus) {
        const aPending = options.getPendingStatus(a);
        const bPending = options.getPendingStatus(b);

        // First, sort by pending status
        if (aPending !== bPending) {
          return aPending ? 1 : -1;
        }

        // If both are pending, check platform existence
        if (aPending && bPending && options.userExistsOnPlatform) {
          const aExistsOnPlatform = options.userExistsOnPlatform(a);
          const bExistsOnPlatform = options.userExistsOnPlatform(b);

          // Among pending users, prioritise those who exist on platform
          if (aExistsOnPlatform !== bExistsOnPlatform) {
            return aExistsOnPlatform ? -1 : 1;
          }
        }
      }

      // Sort by role order
      const aRoleCode = options.getRoleCode(a);
      const bRoleCode = options.getRoleCode(b);

      const indexA = order.indexOf(aRoleCode);
      const indexB = order.indexOf(bRoleCode);

      // If both are in the order array, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only one is in the order array, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;

      // If neither is in the order array, maintain original order
      return 0;
    }

    // Default behavior: sorting role codes directly
    const indexA = order.indexOf(a as RoleCode);
    const indexB = order.indexOf(b as RoleCode);

    // If both are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one is in the order array, prioritize it
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the order array, maintain original order
    return 0;
  });
};

/**
 * Returns the localized display name for a role code
 * @param code The role code to get the display name for
 * @returns The localized display name or the code itself if not found
 */
export const getRoleDisplayName = (code?: RoleCode): string => {
  if (!code) {
    return '';
  }

  const allRoleNames: Record<string, string> = {
    // Project roles
    [ProjRoles.PROJ_MNGR]: t('roles.project.manager'),
    [ProjRoles.PROJ_MEMB]: t('roles.project.member'),
    [ProjRoles.PROJ_PROF]: t('roles.project.professional'),
    [ProjRoles.PROJ_PROC_OFF]: t('roles.project.procOff'),
    [ProjRoles.PROJ_CLI_BASE]: t('roles.project.client'),
    [ProjRoles.PROJ_CLI_APRV]: t('roles.project.clientWithApprovals'),

    // Site roles
    [SiteRoles.SITE_ADMIN]: t('roles.site.admin'),
    [SiteRoles.SITE_MEMBER]: t('roles.site.member'),
    [SiteRoles.SITE_PROC_OFF]: t('roles.site.procOff'),
    [SiteRoles.SITE_FIN_OFF]: t('roles.site.finOff'),
    [SiteRoles.SITE_FLD_USR]: t('roles.site.fieldUser'),
    [SiteRoles.SITE_PROF]: t('roles.site.professional'),
    [SiteRoles.SITE_CLI_BASE]: t('roles.site.client'),
    [SiteRoles.SITE_CLI_APRV]: t('roles.site.clientWithApproval'),

    // Org roles
    [OrgRoles.ORG_ADMIN]: t('roles.org.admin'),
    [OrgRoles.ORG_OWN]: t('roles.org.owner'),
    [OrgRoles.ORG_FO]: t('roles.org.finOff'),
    [OrgRoles.ORG_FA]: t('roles.org.finAdmin'),
    [OrgRoles.ORG_MEMB]: t('roles.org.member'),

    // BOQ roles
    [BOQRoles.BOQ_OWN]: t('roles.boq.owner'),
    [BOQRoles.BOQ_ADMIN]: t('roles.boq.admin'),
    [BOQRoles.BOQ_CAT_ADMIN]: t('roles.boq.catalogAdmin'),
  };

  return allRoleNames[String(code)] || String(code);
};

const PROJECT_SHORT_ROLE_KEYS: Record<ProjRoles, string> = {
  [ProjRoles.PROJ_MNGR]: 'roles.project.short.manager',
  [ProjRoles.PROJ_PROF]: 'roles.project.short.professional',
  [ProjRoles.PROJ_PROC_OFF]: 'roles.project.short.procOff',
  [ProjRoles.PROJ_MEMB]: 'roles.project.short.member',
  [ProjRoles.PROJ_CLI_BASE]: 'roles.project.short.client',
  [ProjRoles.PROJ_CLI_APRV]: 'roles.project.short.clientWithApprovals',
};

/**
 * The project-role name without its scope — "Manager", not "Project Manager". Use this
 * rather than stripping the prefix off {@link getRoleDisplayName}, which only works while
 * the prefix leads the string — an accident of English. Falls back to the full name when a
 * role has no short copy.
 */
export const getProjectRoleShortName = (code?: RoleCode): string => {
  if (!code) {
    return '';
  }

  const key = PROJECT_SHORT_ROLE_KEYS[code as ProjRoles];

  return key && i18n.global.te(key) ? t(key) : getRoleDisplayName(code);
};

/**
 * Returns the localized description for a role code
 * @param type The type of role ('site', 'project', 'org', or 'boq')
 * @param code The role code to get the description for
 * @returns The localized description
 */
export const getRoleDescription = (type: RoleType, code: RoleCode): string => {
  if (type === 'project') {
    const projectRoleDescriptions: Record<ProjRoles, string> = {
      [ProjRoles.PROJ_MNGR]: t('roles.project.managerDescription'),
      [ProjRoles.PROJ_PROF]: t('roles.project.professionalDescription'),
      [ProjRoles.PROJ_PROC_OFF]: t('roles.project.procOffDescription'),
      [ProjRoles.PROJ_CLI_BASE]: t('roles.project.clientDescription'),
      [ProjRoles.PROJ_CLI_APRV]: t('roles.project.clientWithApprovalsDescription'),
      [ProjRoles.PROJ_MEMB]: t('roles.project.memberDescription'),
    };
    return projectRoleDescriptions[code as ProjRoles] || '';
  }

  if (type === 'site') {
    const siteRoleDescriptions: Record<SiteRoles, string> = {
      [SiteRoles.SITE_ADMIN]: t('roles.site.adminDescription'),
      [SiteRoles.SITE_PROC_OFF]: t('roles.site.procOffDescription'),
      [SiteRoles.SITE_FIN_OFF]: t('roles.site.finOffDescription'),
      [SiteRoles.SITE_FLD_USR]: t('roles.site.fieldUserDescription'),
      [SiteRoles.SITE_PROF]: t('roles.site.professionalDescription'),
      [SiteRoles.SITE_CLI_BASE]: t('roles.site.clientDescription'),
      [SiteRoles.SITE_CLI_APRV]: t('roles.site.clientWithApprovalDescription'),
      [SiteRoles.SITE_MEMBER]: t('roles.site.memberDescription'),
    };
    return siteRoleDescriptions[code as SiteRoles] || '';
  }

  if (type === 'org') {
    const orgRoleDescriptions: Record<OrgRoles, string> = {
      [OrgRoles.ORG_ADMIN]: t('roles.org.adminDescription'),
      [OrgRoles.ORG_OWN]: t('roles.org.ownerDescription'),
      [OrgRoles.ORG_FO]: t('roles.org.finOffDescription'),
      [OrgRoles.ORG_FA]: t('roles.org.finAdminDescription'),
      [OrgRoles.ORG_MEMB]: t('roles.org.memberDescription'),
    };
    return orgRoleDescriptions[code as OrgRoles] || '';
  }

  if (type === 'boq') {
    const boqRoleDescriptions: Record<BOQRoles, string> = {
      [BOQRoles.BOQ_OWN]: t('roles.boq.ownerDescription'),
      [BOQRoles.BOQ_ADMIN]: t('roles.boq.adminDescription'),
      [BOQRoles.BOQ_CAT_ADMIN]: t('roles.boq.catalogAdminDescription'),
    };
    return boqRoleDescriptions[code as BOQRoles] || '';
  }

  return '';
};

/**
 * Returns the localized display name and description for a role code in a list
 * @param type The type of role ('site', 'project', 'org', or 'boq')
 * @param code The role code to get the display data for
 * @returns An object with title and description or null if the code doesn't belong to the specified type
 */
export const getRoleDisplayDataForList = (
  type: RoleType,
  code: RoleCode,
): {
  title: string;
  description: string;
} | null => {
  // Check if the code belongs to the specified type
  const belongsToType = (() => {
    switch (type) {
      case 'project': {
        return Object.values(ProjRoles).includes(code as ProjRoles);
      }
      case 'site': {
        return Object.values(SiteRoles).includes(code as SiteRoles);
      }
      case 'org': {
        return Object.values(OrgRoles).includes(code as OrgRoles);
      }
      case 'boq': {
        return Object.values(BOQRoles).includes(code as BOQRoles);
      }
      default: {
        return false;
      }
    }
  })();

  if (belongsToType) {
    return {
      title: getRoleDisplayName(code),
      description: getRoleDescription(type, code),
    };
  }

  return null;
};

// checks for BOQ level permissions (BOQAdmin / BOQOwner)
export const hasBOQPermissions = (permissions: BOQRoles[]): boolean => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  return permissions.some((code) =>
    loggedInUser.value?.permissions?.some((permission) => permission.code === code),
  );
};

// checks for ORG level permissions (ORGAdmin / ORGOwner / ORGMember)
export const hasOrgPermissions = (permissions: OrgRoles[]): boolean => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  // Read the active org's full role set (same source as checkDirectRoles / CASL)
  // so multi-role users match on any held role. Flag-independent — the array has
  // one entry for single-role and N for multi-role. Returns false (never throws)
  // when roles.organisations is absent (cold start).
  const org = loggedInUser.value?.roles?.organisations?.find((o) => o.id === selectedOrgId.value);

  return !!org && permissions.some((role) => org.roles.includes(role));
};

export const selectedOrgId = computed(() => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  return loggedInUser.value?.selectedOrg?.id;
});

// The active org's full role set — the same source hasOrgPermissions / checkDirectRoles
// read, so analytics and permissions never disagree about a user's roles. Returns []
// (never throws) when roles.organisations is absent (cold start).
export const selectedOrgRoles = computed<OrgRoles[]>(() => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  const org = loggedInUser.value?.roles?.organisations?.find((o) => o.id === selectedOrgId.value);

  return (org?.roles ?? []) as OrgRoles[];
});

export const loggedInUserOrgList = computed(() => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  return loggedInUser.value?.organisations ?? [];
});

// checks for lower level permissions (SITE & PROJ levels)
export const hasLowerLevelPermissions = (
  type: 'project' | 'site',
  id: string,
  permission: string,
): boolean => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  const { roles } = loggedInUser.value;
  const org = roles?.organisations.find((org) => org.id === selectedOrgId.value!);

  const permissions = type === 'project' ? org?.projects : org?.sites;

  const permissionToFind = permissions?.find((item) => item.id === id);

  return permissionToFind ? permissionToFind.roles.includes(permission) : false;
};

// checks for lower level permissions on an organisation level, not site/project specific (SITE & PROJ)
export const hasOrgLowerLevelPermissions = (
  type: 'project' | 'site',
  permission: string,
): boolean => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  const { roles } = loggedInUser.value;
  const org = roles?.organisations.find((org) => org.id === selectedOrgId.value!);

  if (type === 'site' && org) {
    return org.sites.some((site) => site.roles.includes(permission));
  } else if (type === 'project' && org) {
    return org.projects.some((project) => project.roles.includes(permission));
  }

  return false;
};

export const hasOrgMembRoleWithNoOtherRoles = (): boolean => {
  const authStore = useAuthStore();
  const { loggedInUser } = storeToRefs(authStore);

  const { roles } = loggedInUser.value;
  const org = roles?.organisations.find((org) => org.id === selectedOrgId.value!);

  if (!org) return false;

  // A true "bare member": ORG_MEMB is the user's ONLY org-level role. Any other org role
  // (ORG_FO/ORG_FA/ORG_ADMIN/ORG_OWN) grants standalone org access (dashboard, accounts,
  // projects visibility), so such a user is not a bare member and must not be routed to the
  // no-projects-or-sites empty state. Previously this only checked `includes('ORG_MEMB')`,
  // which misclassified e.g. a finance officer (ORG_MEMB + ORG_FO) as a bare member.
  const isOnlyOrgMembRole =
    org.roles.includes(OrgRoles.ORG_MEMB) && org.roles.every((role) => role === OrgRoles.ORG_MEMB);

  const noSiteRoles = org.sites.every((site) => site.roles.length === 0);

  const noProjectRoles = org.projects.every((project) => project.roles.length === 0);

  return isOnlyOrgMembRole && noSiteRoles && noProjectRoles;
};
